import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import * as Sentry from "@sentry/browser";
import { getApiUrl } from "@utils/baseUrl";
import type { ConversationsListResponse, ConversationMessagesResponse, TitleResponse, FeedbackResponse, HermesRootState } from "@containers/Hermes/types";

const hermesUrl = (path: string): string => getApiUrl(`hermes/${path}`);

export const loadConversations = createAsyncThunk<ConversationsListResponse, void, { rejectValue: string }>("hermes/loadConversations", async (_, { rejectWithValue }) => {
  try {
    const { data } = await axios.get<ConversationsListResponse>(hermesUrl("conversations"), {
      params: { limit: 50, offset: 0 },
    });
    return data;
  } catch (error: any) {
    Sentry.captureException(error);
    return rejectWithValue(error?.response?.data?.message ?? "Failed to load conversations.");
  }
});

export const loadMoreConversations = createAsyncThunk<ConversationsListResponse, void, { state: HermesRootState; rejectValue: string }>("hermes/loadMoreConversations", async (_, { getState, rejectWithValue }) => {
  try {
    const { conversationsOffset } = getState().hermes;
    const { data } = await axios.get<ConversationsListResponse>(hermesUrl("conversations"), {
      params: { limit: 50, offset: conversationsOffset },
    });
    return data;
  } catch (error: any) {
    Sentry.captureException(error);
    return rejectWithValue(error?.response?.data?.message ?? "Failed to load conversations.");
  }
});

export const loadConversation = createAsyncThunk<ConversationMessagesResponse, string, { rejectValue: string }>("hermes/loadConversation", async (conversationId, { rejectWithValue }) => {
  try {
    const { data } = await axios.get<ConversationMessagesResponse>(hermesUrl(`conversations/${conversationId}/messages`));
    return data;
  } catch (error: any) {
    Sentry.captureException(error);
    return rejectWithValue(error?.response?.data?.message ?? "Failed to load conversation.");
  }
});

export type PostFeedbackArgs = {
  conversationId: string;
  messageId: number;
  helpful: boolean;
  comment?: string;
};

export const postFeedback = createAsyncThunk<{ conversationId: string; messageId: number; helpful: boolean }, PostFeedbackArgs, { rejectValue: string }>("hermes/postFeedback", async ({ conversationId, messageId, helpful, comment }, { rejectWithValue }) => {
  try {
    const body: { message_id: number; helpful: boolean; comment?: string } = { message_id: messageId, helpful };
    if (comment !== undefined) body.comment = comment;
    await axios.post<FeedbackResponse>(hermesUrl(`conversations/${conversationId}/feedback`), body);
    return { conversationId, messageId, helpful };
  } catch (error: any) {
    Sentry.captureException(error);
    return rejectWithValue(error?.response?.data?.message ?? "Failed to submit feedback.");
  }
});

export type RenameConversationArgs = { conversationId: string; title: string };

export const renameConversation = createAsyncThunk<{ conversationId: string; title: string }, RenameConversationArgs, { rejectValue: string }>("hermes/renameConversation", async ({ conversationId, title }, { rejectWithValue }) => {
  try {
    const { data } = await axios.post<TitleResponse>(hermesUrl(`conversations/${conversationId}/title`), { title });
    return { conversationId, title: data.title };
  } catch (error: any) {
    Sentry.captureException(error);
    return rejectWithValue(error?.response?.data?.message ?? error?.response?.data?.error ?? "Failed to rename conversation.");
  }
});

export const deleteConversation = createAsyncThunk<{ conversationId: string }, string, { rejectValue: string }>("hermes/deleteConversation", async (conversationId, { rejectWithValue }) => {
  try {
    await axios.delete(hermesUrl(`conversations/${conversationId}`));
    return { conversationId };
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return { conversationId };
    }
    Sentry.captureException(error);
    return rejectWithValue(error?.response?.data?.message ?? "Failed to delete conversation.");
  }
});
